<template>
  <v-container fluid class="evaluation-view mt-6">
    <!-- Evaluation Header Card -->
    <v-card class="mb-8 evaluation-header-card" elevation="1">
      <div class="evaluation-header-content">
        <div class="evaluation-header-left">
          <v-icon class="mb-2 evaluation-icon" size="36" color="primary">mdi-file-document-outline</v-icon>
          <h1 class="text-h4 primary--text evaluation-title">{{ evaluation.title }}</h1>
          <v-chip :color="getStatusColor(evaluation.status)" small class="ml-2 mt-1" outlined>
            {{ evaluation.status }}
          </v-chip>
        </div>
        <div class="evaluation-details">
          <div class="evaluation-period">
            <span class="period-label">Evaluation Period: </span>
            <span class="period-dates">{{ formatDate(evaluation.startDate) }} - {{ formatDate(evaluation.endDate)
              }}</span>
          </div>
        </div>
      </div>
      <v-divider class="my-2"></v-divider>
      <v-card-text>
        <div class="evaluation-info">
          <div class="info-item">
            <span class="info-label">Year: </span>
            <span class="info-value">{{ evaluation.session?.name || 'No year' }}</span>
          </div>
          <div class="info-item">
            <span class="info-label">Evaluatee:</span>
            <span class="info-value">{{ evaluation.evaluatee ? evaluation.evaluatee.name : 'Not Assigned' }}</span>
          </div>
          <div class="info-item">
            <span class="info-label">Template:</span>
            <span class="info-value">{{ evaluation.template ? evaluation.template.name : 'Not Selected' }}</span>
          </div>
        </div>
      </v-card-text>
    </v-card>

    <!-- Progress Tracking -->
    <v-card v-if="evaluationStatusInfo" class="mb-6 progress-card" elevation="1">
      <v-card-text>
        <div class="d-flex align-center">
          <div class="flex-grow-1">
            <div class="text-subtitle-1 mb-2">Evaluation Progress</div>
            <v-progress-linear
              :value="(evaluationStatusInfo.submittedSections / evaluationStatusInfo.totalSections) * 100" height="25"
              rounded :color="evaluationStatusInfo.allSectionsSubmitted ? 'success' : 'primary'">
              <template v-slot:default="{ value }">
                <strong>{{ Math.round(value) }}%</strong>
              </template>
            </v-progress-linear>
            <div class="text-caption mt-2">
              {{ evaluationStatusInfo.submittedSections }} of {{ evaluationStatusInfo.totalSections }} sections
              submitted
            </div>
          </div>
          <div class="ml-4">
            <v-icon v-if="evaluationStatusInfo.allSectionsSubmitted" color="success" size="48">
              mdi-check-circle-outline
            </v-icon>
          </div>
        </div>
      </v-card-text>
    </v-card>

    <!-- No Sections Alert -->
    <v-alert v-if="!template.sections || template.sections.length === 0" type="info" class="mt-4 paper-alert" outlined
      dense color="primary" icon="mdi-information-outline">
      This evaluation has no sections to complete.
    </v-alert>

    <!-- Sections -->
    <div v-else class="sections-container">
      <v-card v-for="(section, sectionIndex) in template.sections" :key="section.id"
        class="mb-8 section-card paper-card" elevation="1">
        <div class="section-header" @click="toggleSection(sectionIndex)" style="cursor: pointer;">
          <div class="section-header-content">
            <v-icon left color="primary">mdi-file-document-outline</v-icon>
            <span class="section-title">Section {{ sectionIndex + 1 }}: {{ section.name }}</span>
            <v-chip v-if="section.role" small outlined color="primary" class="ml-2">
              {{ section.role }}
            </v-chip>

            <!-- Section Status Indicator -->
            <v-chip :color="getSectionStatusDisplay(getSectionStatus(Number(section.id))).color" small class="ml-2"
              outlined>
              <v-icon left small>{{ getSectionStatusDisplay(getSectionStatus(Number(section.id))).icon }}</v-icon>
              {{ getSectionStatusDisplay(getSectionStatus(Number(section.id))).label }}
            </v-chip>

            <!-- Section Score -->
            <v-chip
              v-if="section.role && getSectionScore(Number(section.id)) !== null"
              color="amber darken-2"
              small
              class="ml-2"
              outlined
            >
              <v-icon left small>mdi-star</v-icon>
              Score: {{ getSectionScore(Number(section.id)).toFixed(1) }}
            </v-chip>

            <span v-if="!canEditSection(section)" class="pl-2"><v-icon small class="mb-1">mdi-lock</v-icon></span>
            <v-spacer></v-spacer>
            <v-icon color="primary" class="expand-icon">
              {{ isSectionExpanded(sectionIndex) ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
            </v-icon>
          </div>
        </div>

        <v-expand-transition>
          <v-card-text v-show="isSectionExpanded(sectionIndex)" class="section-content">
            <p v-if="section.description" class="section-description">{{ section.description }}</p>

            <!-- Section Not Started Alert -->
            <v-alert
              v-if="getSectionStatus(Number(section.id)) === 'PENDING' && section.fields && section.fields.length > 0"
              type="info" text dense color="primary" class="paper-alert mb-4">
              <v-icon left small>mdi-information-outline</v-icon>
              This section has not been started yet. Click "Start This Section" below to begin.
            </v-alert>

            <!-- No Fields Alert -->
            <v-alert v-if="!section.fields || section.fields.length === 0" type="info" text dense color="primary"
              class="paper-alert">
              No fields in this section.
            </v-alert>

            <!-- Fields -->
            <div v-else class="fields-container">
              <v-hover v-for="field in section.fields" :key="field.id">
                <template v-slot:default="{ hover }">
                  <div class="field-item" :class="{ 'field-hover': hover && canEdit }">
                    <component :is="getFieldComponent(field.fieldType)" :field="field"
                      :response="getFieldResponse(Number(field.id))" :disabled="!canEditField(field, section)"
                      @update:response="updateResponse($event, Number(field.id))"></component>
                  </div>
                </template>
              </v-hover>
            </div>

            <!-- Section Actions -->
            <div v-if="canEdit && section.canEdit" class="mt-4">
              <v-divider class="mb-4"></v-divider>

              <!-- Start Section Button -->
              <v-btn v-if="getSectionStatus(Number(section.id)) === 'PENDING'" color="primary" outlined small
                @click="startSection(section)">
                <v-icon left small>mdi-play-circle-outline</v-icon>
                Start This Section
              </v-btn>

              <!-- Submit Section Button -->
              <v-btn v-if="getSectionStatus(Number(section.id)) === 'DRAFT'" color="success" outlined small
                @click="submitSectionDialog(section)">
                <v-icon left small>mdi-check-circle-outline</v-icon>
                Submit This Section
              </v-btn>
            </div>
          </v-card-text>
        </v-expand-transition>
      </v-card>
    </div>

    <!-- Action Buttons -->
    <div class="evaluation-actions">
      <!--      <v-btn-->
      <!--        v-if="canEdit"-->
      <!--        color="info"-->
      <!--        outlined-->
      <!--        class="action-btn"-->
      <!--        :disabled="isSubmitting"-->
      <!--        @click="saveDraft"-->
      <!--      >-->
      <!--        <v-icon left>mdi-content-save-outline</v-icon> Save Draft-->
      <!--      </v-btn>-->
      <div style="display: flex; flex-basis: auto; gap: 16px; flex-wrap: wrap">
        <v-btn color="error" outlined class="action-btn" :disabled="isSubmitting" @click="cancel">
          Close
        </v-btn>
        <v-btn v-if="canSubmit" color="success" class="action-btn primary-action" :loading="isSubmitting"
          @click="submitEvaluation">
          <v-icon left>mdi-check-circle-outline</v-icon> Submit Evaluation
        </v-btn>

        <!-- View Summary Button for submitted evaluations -->
        <v-btn
          v-if="evaluation.status === 'SUBMITTED' || evaluation.status === 'REVIEWED' || evaluation.status === 'APPROVED'"
          color="primary" outlined class="action-btn" @click="viewSummary">
          <v-icon left>mdi-file-document-outline</v-icon> View Summary
        </v-btn>

        <!-- Download Department Report Button (for authorized roles) -->
        <!--        <v-btn-->
        <!--          v-if="canDownloadDepartmentReport"-->
        <!--          color="info"-->
        <!--          outlined-->
        <!--          class="action-btn"-->
        <!--          :loading="isDownloadingReport"-->
        <!--          @click="showDownloadDialog = true"-->
        <!--        >-->
        <!--          <v-icon left>mdi-download</v-icon> Download Department Report-->
        <!--        </v-btn>-->
      </div>

      <!-- Read-only message for submitted evaluations -->
      <v-alert v-if="!canEdit" type="info" class="mt-4 mx-auto" max-width="600" outlined>
        <!--        <v-icon left>mdi-information-outline</v-icon>-->
        This evaluation has been {{ evaluation.status.toLowerCase() }} and cannot be modified.
      </v-alert>
    </div>

    <!-- Submission Confirmation Dialog -->
    <v-dialog v-model="showSubmissionDialog" max-width="500" persistent>
      <v-card>
        <v-card-title class="headline">
          <v-icon left color="warning">mdi-alert-circle-outline</v-icon>
          Confirm Submission
        </v-card-title>
        <v-card-text>
          <p>Are you sure you want to submit this evaluation?</p>
          <p class="text-caption text--secondary mt-2">
            Once submitted, you will not be able to make further changes unless the evaluation is returned for revision.
          </p>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="showSubmissionDialog = false" :disabled="isSubmitting">
            Cancel
          </v-btn>
          <v-btn color="success" @click="confirmSubmission" :loading="isSubmitting">
            <v-icon left>mdi-check</v-icon>
            Yes, Submit
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Section Submission Confirmation Dialog -->
    <v-dialog v-model="showSectionSubmissionDialog" max-width="500" persistent>
      <v-card>
        <v-card-title class="headline">
          <v-icon left color="warning">mdi-alert-circle-outline</v-icon>
          Submit Section
        </v-card-title>
        <v-card-text>
          <p>Are you sure you want to submit <strong>{{ selectedSectionForSubmission ?
            selectedSectionForSubmission.sectionName : '' }}</strong>?</p>
          <p class="text-caption text--secondary mt-2">
            Once submitted, this section cannot be edited.
          </p>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="showSectionSubmissionDialog = false" :disabled="isSubmitting">
            Cancel
          </v-btn>
          <v-btn color="success" @click="confirmSectionSubmission" :loading="isSubmitting">
            <v-icon left>mdi-check</v-icon>
            Submit Section
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Progress Dialog for Saving -->
    <v-dialog v-model="isSubmitting" persistent max-width="400">
      <v-card>
        <v-card-text class="text-center py-6">
          <v-progress-circular indeterminate color="primary" size="60" class="mb-4"></v-progress-circular>
          <div class="text-h6">{{ submitProgressText }}</div>
          <div class="text-caption text--secondary mt-2">Please wait...</div>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Department Report Download Dialog -->
    <v-dialog v-model="showDownloadDialog" max-width="500">
      <v-card>
        <v-card-title class="headline">
          <v-icon left color="info">mdi-download</v-icon>
          Download Department Report
        </v-card-title>
        <v-card-text>
          <v-form ref="downloadForm">
            <v-select v-model="downloadYear" :items="yearOptions" label="Select Year" outlined dense
              :rules="[v => !!v || 'Year is required']"></v-select>
            <div class="text-caption text--secondary mt-2">
              Note: The report will be generated for your department{{ userDepartmentName ? ` (${userDepartmentName})` :
              ''
              }}.
            </div>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="showDownloadDialog = false">
            Cancel
          </v-btn>
          <v-btn color="info" @click="downloadReport" :loading="isDownloadingReport" :disabled="!downloadYear">
            <v-icon left>mdi-download</v-icon>
            Download
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

  </v-container>
</template>

<script>
// Response Field Components
import TextFieldResponse from './components/fields/response/TextFieldResponse.vue';
import TextareaFieldResponse from './components/fields/response/TextareaFieldResponse.vue';
import NumberFieldResponse from './components/fields/response/NumberFieldResponse.vue';
import DateFieldResponse from './components/fields/response/DateFieldResponse.vue';
import SelectFieldResponse from './components/fields/response/SelectFieldResponse.vue';
import CheckboxFieldResponse from './components/fields/response/CheckboxFieldResponse.vue';
import RadioFieldResponse from './components/fields/response/RadioFieldResponse.vue';
import RatingFieldResponse from './components/fields/response/RatingFieldResponse.vue';
import MultiTextFieldResponse from './components/fields/response/MultiTextFieldResponse.vue';
import MultiFieldResponse from './components/fields/response/MultiFieldResponse.vue';
import SignatureFieldResponse from './components/fields/response/SignatureFieldResponse.vue';
import SectionScoreFieldResponse from './components/fields/response/SectionScoreFieldResponse.vue';

import AppView from '@/layout/AppView.vue';

import { Component, Vue, Prop } from 'vue-property-decorator';
import { Template, Field, FieldType, SectionStatus, SectionResponseDTO, EvaluationStatusInfo } from '@/shared/types/aperTemplate';
import { Evaluation, EvaluationStatus, ResponseDTO } from '@/shared/apis/aper/aperEvaluations';
import {
  getEvaluationById,
  getEvaluationResponses,
  saveResponse,
  updateEvaluationStatus,
  downloadDepartmentEvaluationReport
} from '@/shared/apis/aper/aperEvaluations';
import { getSectionsApi } from '@/shared/apis/aper/aperSections';
import { getSectionResponses, submitSection, getEvaluationSectionsStatus, markSectionAsDraft } from '@/shared/apis/aper/aperSectionResponses';
import { formatDateForDisplay } from '@/utils/dateUtils';
import Roles from "@/shared/Roles";

export default @Component({
  components: {
    TextFieldResponse,
    TextareaFieldResponse,
    NumberFieldResponse,
    DateFieldResponse,
    SelectFieldResponse,
    CheckboxFieldResponse,
    RadioFieldResponse,
    RatingFieldResponse,
    MultiTextFieldResponse,
    MultiFieldResponse,
    SignatureFieldResponse,
    SectionScoreFieldResponse,
    AppView
  }
}) 
class EvaluationView extends Vue {
  @Prop({ type: String, required: true }) id;

  evaluation = {
    title: '',
    startDate: '',
    endDate: '',
    status: EvaluationStatus.DRAFT,
    template: {
      id: 0,
      name: ''
    },
    evaluator: {
      id: 0,
      name: ''
    },
    evaluatee: {
      id: 0,
      name: ''
    }
  };

  template = {
    id: 0,
    name: '',
    description: '',
    version: '',
    active: false,
    sections: []
  };

  responses = [];
  fieldResponses = new Map();
  sectionResponses = [];
  sectionResponseMap = new Map();
  evaluationStatusInfo = null;
  isLoading = false;
  isSubmitting = false;
  showSubmissionDialog = false;
  showSectionSubmissionDialog = false;
  selectedSectionForSubmission = null;
  submitProgressText = 'Saving responses...';
  expandedSections = {};
  isDownloadingReport = false;
  showDownloadDialog = false;
  downloadYear = null;

  created() {
    this.fetchEvaluation();
  }

  getSectionStatus(sectionId) {
    const sectionResponse = this.sectionResponseMap.get(sectionId);
    return sectionResponse ? sectionResponse.status : SectionStatus.PENDING;
  }

  getSectionScore(sectionId) {
    const sectionResponse = this.sectionResponseMap.get(sectionId);
    return sectionResponse && sectionResponse.score !== undefined ? sectionResponse.score : null;
  }

  canEditSection(section) {
    const sectionResponse = this.sectionResponseMap.get(Number(section.id));
    return section.canEdit && (!sectionResponse || sectionResponse.canEdit);
  }

  canEditField(field, section) {
    if (!this.canEdit) return false;
    if (!this.canEditSection(section)) return false;
    if (field.canEdit === false) return false;
    return true;
  }

  getSectionStatusDisplay(status) {
    switch (status) {
      case SectionStatus.PENDING:
        return { label: 'Not Started', color: 'grey', icon: 'mdi-circle-outline' };
      case SectionStatus.DRAFT:
        return { label: 'In Progress', color: 'blue', icon: 'mdi-pencil' };
      case SectionStatus.SUBMITTED:
        return { label: 'Submitted', color: 'success', icon: 'mdi-check-circle' };
      default:
        return { label: 'Unknown', color: 'grey', icon: 'mdi-help-circle' };
    }
  }

  toggleSection(sectionIndex) {
    this.$set(this.expandedSections, sectionIndex, !this.expandedSections[sectionIndex]);
  }

  isSectionExpanded(sectionIndex) {
    return !!this.expandedSections[sectionIndex];
  }

  get currentUser() {
    return this.$store.getters.getUser
  }

  async fetchEvaluation() {
    this.isLoading = true;
    try {
      // Fetch evaluation details
      const evaluationResponse = await getEvaluationById(this.id);
      this.evaluation = evaluationResponse.data;

      // Initialize template with basic info
      this.template = {
        id: this.evaluation.template.id,
        name: this.evaluation.template.name,
        description: '',
        version: '',
        active: true,
        sections: []
      };

      // Fetch template sections
      if (this.evaluation.template && this.evaluation.template.id) {
        try {
          const sectionsResponse = await getSectionsApi(String(this.evaluation.template.id), String(this.evaluation.id));
          this.template.sections = sectionsResponse.data;
          // Expand the first section by default
          if (this.template.sections.length > 0) {
            // this.$set(this.expandedSections, 0, true);
          }
        } catch (sectionError) {
          console.error('Error fetching template sections:', sectionError);
        }
      }

      // Fetch existing responses
      const responsesResponse = await getEvaluationResponses(this.id);
      this.responses = responsesResponse.data;

      // Organize responses by field ID
      this.responses.forEach(response => {
        if (response.fieldId) {
          this.fieldResponses.set(response.fieldId, response);
        }
      });

      // Fetch section responses to get status information
      try {
        this.sectionResponses = await getSectionResponses(Number(this.id));
        this.sectionResponses.forEach(sr => {
          this.sectionResponseMap.set(sr.sectionId, sr);

          // Also populate fieldResponses from section responses (has more complete data including signatures)
          if (sr.responses && sr.responses.length > 0) {
            sr.responses.forEach(response => {
              if (response.fieldId) {
                // Merge with existing response or set new one
                const existingResponse = this.fieldResponses.get(response.fieldId);
                if (existingResponse) {
                  // Merge - prefer non-null values from section response
                  const merged = { ...existingResponse };
                  Object.keys(response).forEach(key => {
                    if (response[key] !== null && response[key] !== 'null') {
                      merged[key] = response[key];
                    }
                  });
                  this.fieldResponses.set(response.fieldId, merged);
                } else {
                  this.fieldResponses.set(response.fieldId, response);
                }
              }
            });
          }
        });
      } catch (error) {
        console.log('Section responses not available yet');
      }

      // Fetch evaluation status info
      try {
        this.evaluationStatusInfo = await getEvaluationSectionsStatus(Number(this.id));
      } catch (error) {
        console.log('Evaluation status info not available yet');
      }
    } catch (error) {
      console.error('Error fetching evaluation:', error);
      this.$toast.error('Failed to load evaluation');
    } finally {
      this.isLoading = false;
    }
  }

  getFieldComponent(fieldType) {
    const componentMap = {
      [FieldType.TEXT]: 'text-field-response',
      [FieldType.TEXTAREA]: 'textarea-field-response',
      [FieldType.NUMBER]: 'number-field-response',
      [FieldType.DATE]: 'date-field-response',
      [FieldType.SINGLE_SELECT]: 'select-field-response',
      [FieldType.MULTI_SELECT]: 'select-field-response',
      [FieldType.CHECKBOX]: 'checkbox-field-response',
      [FieldType.RADIO]: 'radio-field-response',
      [FieldType.RATING]: 'rating-field-response',
      [FieldType.MULTI_TEXT]: 'multi-text-field-response',
      [FieldType.MULTI_FIELD]: 'multi-field-response',
      [FieldType.SIGNATURE]: 'signature-field-response',
      [FieldType.SECTION_SCORE]: 'section-score-field-response'
    };
    return componentMap[fieldType] || 'text-field-response';
  }

  getFieldResponse(fieldId) {
    return this.fieldResponses.get(fieldId);
  }

  updateResponse(responseData, fieldId) {
    // Ensure the fieldId is set
    responseData.fieldId = fieldId;
    this.fieldResponses.set(fieldId, responseData);

    // Auto-save individual response if evaluation is in progress
    if (this.evaluation.status === EvaluationStatus.IN_PROGRESS ||
      this.evaluation.status === EvaluationStatus.DRAFT) {
      this.autoSaveResponse(responseData);
    }
  }

  /**
   * Auto-save individual response with debouncing to avoid too many API calls
   */
  autoSaveTimer = null;

  autoSaveResponse(responseData) {
    // Clear previous timer
    if (this.autoSaveTimer) {
      clearTimeout(this.autoSaveTimer);
    }

    // Set new timer to auto-save after 2 seconds of inactivity
    this.autoSaveTimer = setTimeout(async () => {
      try {
        await saveResponse(this.id, responseData);
        // Optionally show a subtle success indicator
      } catch (error) {
        if (error.message && error.message.includes('submitted section')) {
          this.$toast.error('This section has been submitted and cannot be edited.');
          // Refresh evaluation data
          this.fetchEvaluation();
        } else {
          console.error('Auto-save failed:', error);
        }
      }
    }, 2000);
  }

  /**
   * Validate all required fields before submission
   */
  validateRequiredFields() {
    const missingFields = [];

    // Check all sections and their fields
    if (this.template.sections) {
      for (const section of this.template.sections) {
        if (section.fields) {
          for (const field of section.fields) {
            if (field.required) {
              const response = this.fieldResponses.get(Number(field.id));
              const isEmpty = this.isFieldEmpty(field, response);

              if (isEmpty) {
                missingFields.push(`${section.name}: ${field.label}`);
              }
            }
          }
        }
      }
    }

    return {
      isValid: missingFields.length === 0,
      missingFields
    };
  }

  /**
   * Check if a field response is empty based on field type
   */
  isFieldEmpty(field, response) {
    if (!response) return true;

    switch (field.fieldType) {
      case FieldType.TEXT:
      case FieldType.TEXTAREA:
        return !response.textValue || response.textValue.trim() === '';

      case FieldType.MULTI_TEXT:
        return !response.textValues || response.textValues.length === 0 ||
          response.textValues.every(tv => !tv.value || tv.value.trim() === '');

      case FieldType.MULTI_FIELD:
        return !response.multiFieldValues || response.multiFieldValues.length === 0;

      case FieldType.NUMBER:
      case FieldType.RATING:
        return response.numericValue === undefined || response.numericValue === null;

      case FieldType.DATE:
        return !response.dateValue;

      case FieldType.SINGLE_SELECT:
      case FieldType.MULTI_SELECT:
      case FieldType.CHECKBOX:
      case FieldType.RADIO:
        return !response.selectedOptionIds || response.selectedOptionIds.length === 0;

      case FieldType.SIGNATURE:
        return !response.signatureBase64;

      case FieldType.PREDEFINED:
      case FieldType.SECTION_SCORE:
        return false; // Auto-populated fields are never empty

      default:
        return true;
    }
  }

  /**
   * Check if the evaluation can be edited based on its current status
   */
  get canEdit() {
    return this.evaluation.status === EvaluationStatus.DRAFT ||
      this.evaluation.status === EvaluationStatus.IN_PROGRESS;
  }

  /**
   * Check if the evaluation can be submitted
   */
  get canSubmit() {
    return this.canEdit && !this.isSubmitting && this.evaluation.evaluatee && this.currentUser && this.evaluation.evaluatee.id === this.currentUser.id;
  }

  /**
   * Check if user can download department report
   */
  get canDownloadDepartmentReport() {
    if (!this.currentUser) return false;
    const allowedRoles = ['HEAD', 'DEAN', 'REGISTRAR', 'A_AND_PC', 'ADMIN'];
    return Roles.deepContains(allowedRoles, this.currentUser.roles);
  }

  /**
   * Get year options for the download dialog
   */
  get yearOptions() {
    const currentYear = new Date().getFullYear();
    const years = [];
    // Provide last 5 years as options
    for (let i = 0; i < 5; i++) {
      years.push(currentYear - i);
    }
    return years;
  }

  /**
   * Get user's department name for display
   */
  get userDepartmentName() {
    return this.currentUser && this.currentUser.userInformation && this.currentUser.userInformation.department ? this.currentUser.userInformation.department.name : '';
  }

  getStatusColor(status) {
    const statusColors = {
      [EvaluationStatus.DRAFT]: 'grey',
      [EvaluationStatus.IN_PROGRESS]: 'blue',
      [EvaluationStatus.SUBMITTED]: 'orange',
      [EvaluationStatus.REVIEWED]: 'purple',
      [EvaluationStatus.APPROVED]: 'success',
      [EvaluationStatus.REJECTED]: 'error'
    };

    return statusColors[status] || 'grey';
  }

  /**
   * Formats a date value for display, handling both string dates and Java array format
   * @param dateValue The date value to format (string or array)
   * @returns Formatted date string in the user's locale
   */
  formatDate(dateValue) {
    return formatDateForDisplay(dateValue);
  }

  async submitEvaluation() {
    // Check if all sections are submitted
    if (this.evaluationStatusInfo && !this.evaluationStatusInfo.allSectionsSubmitted) {
      this.$toast.error(`All sections must be submitted before completing the evaluation. ${this.evaluationStatusInfo.totalSections - this.evaluationStatusInfo.submittedSections} section(s) remaining.`);
      return;
    }

    // Validate required fields before submission
    const validation = this.validateRequiredFields();

    if (!validation.isValid) {
      const missingFieldsList = validation.missingFields.join('\n');
      this.$toast.error(`Please complete the following required fields:\n${missingFieldsList}`);
      return;
    }
    this.showSubmissionDialog = true;
  }

  // Show confirmation dialog

  async confirmSubmission() {
    this.showSubmissionDialog = false;
    this.submitProgressText = 'Saving responses...';
    await this.saveResponses(EvaluationStatus.SUBMITTED);
  }


  async saveDraft() {
    await this.saveResponses(EvaluationStatus.DRAFT);
  }

  async saveResponses(newStatus) {
    this.isSubmitting = true;
    this.submitProgressText = newStatus === EvaluationStatus.DRAFT ? 'Saving draft...' : 'Saving responses...';

    try {
      // Convert Map to array for individual saving
      const responsesToSave = Array.from(this.fieldResponses.values());

      // Save all responses individually as there's no batch endpoint
      // const savePromises = responsesToSave.map(response =>
      //   saveResponse(this.id, response)
      // );

      // Wait for all responses to be saved
      // await Promise.all(savePromises);

      // Update progress message for status update
      if (this.evaluation.status !== newStatus) {
        this.submitProgressText = newStatus === EvaluationStatus.SUBMITTED ? 'Submitting evaluation...' : 'Updating status...';
        await updateEvaluationStatus(this.id, newStatus);
        this.evaluation.status = newStatus;
      }

      // Show success message
      this.$toast.success(newStatus === EvaluationStatus.DRAFT
        ? 'Evaluation saved as draft'
        : 'Evaluation submitted successfully');

      // If submitted, redirect to evaluations list
      if (newStatus === EvaluationStatus.SUBMITTED) {
        this.$router.push('/evaluation/evaluations');
      }
    } catch (error) {
      console.error('Error saving evaluation:', error);
      this.$toast.error('Failed to save evaluation');
    } finally {
      this.isSubmitting = false;
    }
  }



  cancel() {
    // Navigate back or to evaluations list
    this.$router.push('/evaluation/evaluations');
  }

  submitSectionDialog(section) {
    // Find section response
    let sectionResponse = this.sectionResponseMap.get(Number(section.id));

    // If no section response exists yet, create a placeholder
    if (!sectionResponse) {
      sectionResponse = {
        id: 0, // Will be assigned by backend
        sectionId: Number(section.id),
        sectionName: section.name,
        status: SectionStatus.DRAFT,
        canEdit: true,
        responses: []
      };
    }

    this.selectedSectionForSubmission = sectionResponse;
    this.showSectionSubmissionDialog = true;
  }

  async confirmSectionSubmission() {
    if (!this.selectedSectionForSubmission) return;

    this.isSubmitting = true;
    this.submitProgressText = 'Submitting section...';
    this.showSectionSubmissionDialog = false;

    try {
      // Submit the section
      const updatedSection = await submitSection(
        Number(this.id),
        this.selectedSectionForSubmission.id
      );

      // Update local state
      this.sectionResponseMap.set(updatedSection.sectionId, updatedSection);

      // Refresh evaluation status
      this.evaluationStatusInfo = await getEvaluationSectionsStatus(Number(this.id));

      this.$toast.success(`Section "${updatedSection.sectionName}" submitted successfully!`);

      // Check if all sections are now submitted
      if (this.evaluationStatusInfo && this.evaluationStatusInfo.allSectionsSubmitted) {
        this.$toast.info('All sections have been submitted. You can now submit the evaluation.');
      }
    } catch (error) {
      console.error('Error submitting section:', error);
      if (error.response && error.response.status === 400) {
        this.$toast.error(error.response.data.message || 'Failed to submit section');
      } else {
        this.$toast.error('Failed to submit section. Please try again.');
      }
    } finally {
      this.isSubmitting = false;
      this.selectedSectionForSubmission = null;
    }
  }

  async startSection(section) {
    this.isSubmitting = true;
    this.submitProgressText = 'Starting section...';

    try {
      // Find or create section response
      let sectionResponse = this.sectionResponseMap.get(Number(section.id));

      if (!sectionResponse) {
        // Create a new section response with DRAFT status
        sectionResponse = {
          id: 0, // Will be assigned by backend
          sectionId: Number(section.id),
          sectionName: section.name,
          status: SectionStatus.DRAFT,
          canEdit: true,
          responses: []
        };
      }

      // Mark section as draft
      const updatedSection = await markSectionAsDraft(
        Number(this.id),
        sectionResponse.id
      );

      // Update local state
      this.sectionResponseMap.set(updatedSection.sectionId, updatedSection);

      this.$toast.success(`Section "${section.name}" has been started. You can now fill in the fields.`);

      // Optionally expand the section
      const sectionIndex = this.template.sections.findIndex(s => s.id === section.id);
      if (sectionIndex !== -1 && !this.expandedSections[sectionIndex]) {
        this.toggleSection(sectionIndex);
      }
    } catch (error) {
      console.error('Error starting section:', error);
      this.$toast.error('Failed to start section. Please try again.');
    } finally {
      this.isSubmitting = false;
    }
  }

  viewSummary() {
    // Navigate to the summary page
    this.$router.push(`/evaluation/evaluations/${this.id}/summary`);
  }

  async downloadReport() {
    if (!this.downloadYear) {
      this.$toast.error('Please select a year');
      return;
    }

    this.isDownloadingReport = true;
    try {
      // Get department ID from current user's information
      const departmentId = this.currentUser && this.currentUser.userInformation && this.currentUser.userInformation.department ? this.currentUser.userInformation.department.id : null;

      const response = await downloadDepartmentEvaluationReport(departmentId, this.downloadYear);

      // Create a blob URL and trigger download
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `department_evaluation_report_${this.downloadYear}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      this.$toast.success('Department report downloaded successfully');
      this.showDownloadDialog = false;
      this.downloadYear = null; // Reset selection
    } catch (error) {
      console.error('Error downloading report:', error);
      if (error.response && error.response.status === 403) {
        this.$toast.error('You do not have permission to download this department\'s report');
      } else {
        this.$toast.error('Failed to download department report');
      }
    } finally {
      this.isDownloadingReport = false;
    }
  }
}
</script>

<style scoped>
.evaluation-view {
  position: relative;
  padding-bottom: 80px;
  /* Space for action buttons */
  font-family: 'Roboto', 'Helvetica Neue', Arial, sans-serif;
}

/* Paper-like styling */
.paper-card {
  background-color: #ffffff;
  border: 1px solid rgba(0, 0, 0, 0.05);
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04), 0 1px 2px rgba(0, 0, 0, 0.08) !important;
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
}

.paper-card:hover {
  box-shadow: 0 3px 6px rgba(0, 0, 0, 0.08), 0 3px 6px rgba(0, 0, 0, 0.12) !important;
}

.paper-alert {
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
}

/* Evaluation Header Styling */
.evaluation-header-card {
  border-radius: 8px;
  overflow: hidden;
  transition: all 0.3s ease;
  background-color: #fff;
  border-left: 4px solid #3f51b5;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04), 0 1px 2px rgba(0, 0, 0, 0.08) !important;
}

/* Progress Card Styling */
.progress-card {
  background-color: #f8f9fa;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04), 0 1px 2px rgba(0, 0, 0, 0.08) !important;
}

.evaluation-header-content {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 20px 20px 0 20px;
}

.evaluation-header-left {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
}

.evaluation-icon {
  margin-right: 12px;
}

.evaluation-title {
  font-weight: 500;
  line-height: 1.2;
  letter-spacing: -0.5px;
}

.evaluation-details {
  background-color: #f8f9fc;
  padding: 8px 16px;
  border-radius: 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}

.period-label {
  font-size: 0.75rem;
  color: rgba(0, 0, 0, 0.6);
  text-transform: uppercase;
  letter-spacing: 1px;
}

.period-dates {
  font-size: 1.1rem;
  font-weight: 500;
  color: #3f51b5;
}

.evaluation-info {
  display: flex;
  flex-wrap: wrap;
  gap: 24px;
  color: rgba(0, 0, 0, 0.8);
}

.info-item {
  display: flex;
  flex-direction: column;
  min-width: 200px;
}

.info-label {
  font-size: 0.85rem;
  color: rgba(0, 0, 0, 0.6);
  margin-bottom: 4px;
}

.info-value {
  font-size: 1rem;
  font-weight: 500;
}

/* Section Styling */
.sections-container {
  perspective: 1000px;
  max-width: 1200px;
  margin: 0 auto;
  /* display: grid;
  grid-template-columns: repeat(auto-fit, minmax(580px, 1fr));
  gap: 24px; */
}

.section-card {
  border-radius: 12px;
  overflow: hidden;
  transition: all 0.25s cubic-bezier(0.4, 0.0, 0.2, 1);
  transform-style: preserve-3d;
  background: linear-gradient(145deg, #ffffff 0%, #fafafa 100%);
  border: 1px solid rgba(0, 0, 0, 0.03);
  position: relative;
}

.section-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: linear-gradient(90deg, #667eea 0%, #764ba2 100%);
  border-radius: 12px 12px 0 0;
}

.section-card:hover {
  transform: translateY(-4px) scale(1.002);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.06), 0 4px 10px rgba(0, 0, 0, 0.04);
}

.section-header {
  border-bottom: 1px solid rgba(0, 0, 0, 0.04);
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(10px);
}

.section-header-content {
  padding: 20px 24px 16px;
  display: flex;
  align-items: center;
  position: relative;
}

.section-title {
  font-size: 1.1rem;
  font-weight: 600;
  margin-left: 12px;
  color: rgba(0, 0, 0, 0.85);
  letter-spacing: -0.2px;
  line-height: 1.3;
}

.section-content {
  padding: 20px 24px 28px;
  background-color: #fdfdfd;
}

.section-description {
  font-style: italic;
  color: rgba(0, 0, 0, 0.6);
  background-color: #f9f9fa;
  border-left: 3px solid #dde2f3;
  padding: 12px 16px;
  border-radius: 0 8px 8px 0;
  margin-bottom: 24px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}

/* Fields Styling */
.fields-container {
  column-count: auto;
  column-width: 420px;
  column-gap: 20px;
  margin-top: 8px;
}

.field-item {
  display: inline-block;
  width: 100%;
  margin-bottom: 16px;
  break-inside: avoid;
  page-break-inside: avoid;
  transition: all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1);
  border-radius: 10px;
  background: linear-gradient(135deg, #ffffff 0%, #fefefe 100%);
  padding: 20px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.03);
  border: 1px solid rgba(0, 0, 0, 0.02);
  position: relative;
  overflow: hidden;
}

.field-item::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 1px;
  background: linear-gradient(90deg, transparent 0%, rgba(102, 126, 234, 0.3) 50%, transparent 100%);
}

.field-hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.06);
  transform: translateY(-1px) scale(1.002);
  background: linear-gradient(135deg, #ffffff 0%, #fafbff 100%);
  border-color: rgba(102, 126, 234, 0.1);
}

/* Evaluation Actions */
.evaluation-actions {
  display: flex;
  justify-content: flex-end;
  flex-direction: column;
  gap: 16px;
  margin-top: 32px;
  position: fixed;
  bottom: 24px;
  right: 24px;
  z-index: 10;
  background: rgba(255, 255, 255, 0.97);
  padding: 16px 24px;
  border-radius: 28px;
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(10px);
}

.action-btn {
  border-radius: 20px;
  font-weight: 500;
  text-transform: none;
  letter-spacing: 0.3px;
  padding: 0 20px;
  height: 40px;
  transition: all 0.2s ease;
}

.action-btn:hover {
  transform: translateY(-1px);
}

.primary-action {
  box-shadow: 0 2px 5px rgba(76, 175, 80, 0.2);
}

.primary-action:hover {
  box-shadow: 0 4px 8px rgba(76, 175, 80, 0.3);
}

/* Responsive Adjustments */
@media (max-width: 960px) {
  .sections-container {
    grid-template-columns: 1fr;
    max-width: 100%;
  }

  .fields-container {
    column-width: 100%;
    column-count: 1;
  }

  .evaluation-header-content {
    flex-direction: column;
  }

  .evaluation-details {
    margin-top: 16px;
    align-self: flex-start;
  }

  .evaluation-actions {
    position: static;
    flex-direction: column;
    width: 100%;
    border-radius: 8px;
    justify-content: center;
  }
}

@media (max-width: 1200px) and (min-width: 961px) {
  .fields-container {
    column-width: 350px;
  }
}

@media (max-width: 600px) {
  .section-header-content {
    flex-wrap: wrap;
    padding: 12px 16px;
  }

  .section-title {
    width: 100%;
    margin-left: 0;
    margin-top: 8px;
  }

  .evaluation-info {
    flex-direction: column;
    gap: 16px;
  }

  .action-btn {
    flex: 1;
    padding: 0 12px;
  }
}

/* Typography enhancements */

/* Ink and paper textures - subtle paper texture */
.paper-card::after {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image: url('data:image/png;base64,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');
  background-color: transparent;
  opacity: 0.02;
  z-index: 0;
  pointer-events: none;
}

.field-item:focus-within {
  border-color: rgba(63, 81, 181, 0.3);
  box-shadow: 0 0 0 3px rgba(63, 81, 181, 0.1);
}

/* Expand icon transition */
.expand-icon {
  transition: transform 0.3s ease;
}

.section-header:hover {
  background-color: rgba(63, 81, 181, 0.02);
}
</style>
